import { createFileRoute, useNavigate } from "@tanstack/react-router";
import useAppStore from "@/store";
import { useTranslate } from "@/hooks/use-translate";
import {
  getNetAmount,
  getProfit,
  getPurchaseTotal,
  getTotal,
} from "@/store/Order";
import type { Order } from "@/store/Order";
import { cn, pick, unPick } from "@/lib/utils";
import { InfoElement, PriceItem } from "@/components/ui/shared/details";
import {
  OrderStatusColors,
  OrderStatusMap,
  OrderDeliveryStatusMap,
  OrderDeliveryStatusColors,
  OrderStatusTransitions,
  OrderDeliveryStatusTransitions,
  OrderStatusActions,
  OrderDeliveryStatusActions,
} from "@/components/order/order-meta";
import { useSimpleForm } from "@/hooks/use-simple-form";
import { useEffect, useRef } from "react";
import { ChevronLeft, Copy, Plus, Printer } from "lucide-react";
import { Toast } from "@/components/ui/toast";
import SimpleTable from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { useReactToPrint } from "react-to-print";
import { OrderReceipt } from "@/components/order/order-receipt";
import { SalesTableColumns } from "@/components/sale/sale-meta";
import { getFullName } from "@/store/User";
import { LightSelectField } from "@/components/ui/form";
import Show from "@/components/ui/show";
import type { PaymentInfo } from "@/store/FinanceAccount";
import { Alert } from "@/components/ui/alert";
import ProcessPayment from "@/components/ui/shared/process-payment";
import PayableTransaction from "@/components/ui/shared/payable-transaction";
import { OrderModals } from "@/components/order/order-modals";
import type { Shop } from "@/store/Shop";

export const Route = createFileRoute("/(dasboard)/orders/orders/$id")({
  component: ViewOrderPage,
});

function ViewOrderPage() {
  const store = useAppStore();
  const { t, formatDate } = useTranslate();
  const navigate = useNavigate();
  const params = Route.useParams();
  const order = store.order.current as Order;

  const form = useSimpleForm({
    status: "",
    delivery_status: "",
    payments: [],
  });

  const contentA5Ref = useRef<HTMLDivElement>(null);
  const contentA6Ref = useRef<HTMLDivElement>(null);

  const printA5 = useReactToPrint({ contentRef: contentA5Ref });
  const printA6 = useReactToPrint({ contentRef: contentA6Ref });

  useEffect(() => {
    const id = params.id as string;
    if (id) {
      store.order.fetchOne(id).then((order) => {
        store.order.setCurrent(order);
        form.setValues((values: any) => ({
          ...values,
          status: order.status,
          delivery_status: order.delivery_status,
        }));
      });
    }
  }, [params.id, store.auth.session]);

  useEffect(() => {
    store.financeAccount.fetch({});
  }, [store.auth.session]);

  const net_amount = () => {
    return getNetAmount({
      sales: order.sales,
      ...pick(order, ["tax", "delivery_fee", "discount"]),
    });
  };

  if (!order) {
    return null;
  }

  const getRemainingAmount = () => {
    return (
      net_amount() -
      form.values.payments
        .map((item: PaymentInfo) => parseInt(item.amount as any))
        .reduce((sum: number, a: number) => sum + a, 0)
    );
  };

  const addPayment = () => {
    form.setValue("payments", [
      ...form.values.payments,
      {
        finance_account_id: "",
        amount: getRemainingAmount(),
        due_date: "",
        payment_date: formatDate(new Date(), "yyyy-MM-dd"),
        amount_given: 0,
        change_given: 0,
      },
    ]);
  };

  const submit = () => {
    const data = unPick(form.values, ["payments"]);

    if (form.values.payments.length > 0) {
      data["payments"] = form.values.payments.map((item: PaymentInfo) => item);
    }

    store.order
      .updateCurrent(data)
      .then(() => {
        window.location.reload();
      })
      .catch(store.errors.catch);
  };

  return (
    <div className="">
      <Toast name="clipboard" message="Copy to clipboard" autoClose={2000} />

      <Alert
        autoClose={3000}
        message={t("Order updated with succes")}
        name={"update_order_success"}
      />

      <OrderModals />

      <div>
        <button
          type="button"
          onClick={() => window.history.back()}
          className="py-2 inline-flex gap-2 items-center justify-center rounded-lg text-sm text-gray-500 hover:text-gray-900 hover:text-gray-900 hover:underline font-semibold"
        >
          <ChevronLeft className="h-4 w-4" />
          <span className="">{t("Back")}</span>
        </button>
      </div>

      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-4">
        {/* <h2 className="text-xl font-semibold md:-mb-4">Order: {order.id}</h2> */}

        <div
          onClick={async () => {
            await navigator.clipboard.writeText(order.id);
            store.display.show("clipboard");
          }}
          className="py-1.5 px-6 rounded-md bg-white border-gray-200 border font-semibold  font-medium hover:text-blue-500 hover:underline text-gray-600 copy-clipboard-parent cursor-pointer"
        >
          <span> {t("Order ID")} : </span>
          {order.id}
          <button
            className="ml-2 flex-shrink-0"
            onClick={async () => {
              await navigator.clipboard.writeText(order.id);
              store.display.show("clipboard");
            }}
          >
            <Copy className="w-4 h-4" />
          </button>
        </div>

        <div className="flex gap-2 flex-wrap">
          <Button onClick={submit}>{t("Save changes")}</Button>

          <Button onClick={printA5} color="outline" className="py-2 text-sm">
            <Printer className="w-4 h-4" />
            {t("Print A5 Receipt")}
          </Button>

          <Button onClick={printA6} color="outline" className="py-2 text-sm">
            <Printer className="w-4 h-4" />
            {t("Print A6 Receipt")}
          </Button>
        </div>
      </div>

      <div className="hidden">
        <div ref={contentA5Ref}>
          <OrderReceipt
            order={order}
            shop={store.auth.session?.currentShop as Shop}
            size="A5"
          />
        </div>
        <div ref={contentA6Ref}>
          <OrderReceipt
            order={order}
            shop={store.auth.session?.currentShop as Shop}
            size="A6"
          />
        </div>
      </div>

      <>{store.errors.render()}</>

      <section className="flex flex-col md:flex-row gap-8 text-sm">
        <aside className="w-full md:w-1/3 ">
          <div className="bg-white rounded-md border border-gray-200 p-6">
            <ul className="space-y-4 mt-2">
              <InfoElement
                detail={{ label: t("Customer"), value: order.customer_name }}
              />
              {[
                { label: t("Date"), value: formatDate(order.created_at) },
                { label: t("Serve by"), value: getFullName(order.user) },
              ].map((detail, index: number) => (
                <InfoElement key={`order-details${index}`} detail={detail} />
              ))}
            </ul>
            <div className="border-b border-gray-300 border-dashed my-4"></div>

            <div className="mt-4 space-x-4">
              <label className="font-medium text-sm block">
                {t("Delivery status")}
              </label>
              <div className="flex items-center gap-4 mt-1">
                <div
                  className={cn(
                    "rounded-full px-4 w-[120px] text-center shrink-0 py-1 text-sm",
                    OrderDeliveryStatusColors[form.values.delivery_status]
                  )}
                >
                  {t(OrderDeliveryStatusMap[form.values.delivery_status])}
                </div>
                <Show
                  when={
                    OrderDeliveryStatusTransitions[order.delivery_status]
                      ?.length > 0
                  }
                >
                  <LightSelectField
                    name="delivery_status"
                    className="w-full"
                    value={form.values.delivery_status}
                    onChange={form.handleChange}
                    options={OrderDeliveryStatusActions.filter((item) =>
                      OrderDeliveryStatusTransitions[
                        order.delivery_status
                      ]?.includes(item.value)
                    )}
                  />
                </Show>
              </div>
            </div>

            <div className="space-x-4 mt-4">
              <label className="font-medium text-sm block">{t("Status")}</label>
              <div className="flex items-center gap-4 mt-1">
                <div
                  className={cn(
                    "rounded-full px-4 w-[120px] text-center shrink-0 py-1 text-sm",
                    OrderStatusColors[form.values.status]
                  )}
                >
                  {t(OrderStatusMap[form.values.status])}
                </div>

                <Show when={OrderStatusTransitions[order.status]?.length > 0}>
                  <LightSelectField
                    name="status"
                    className="w-full"
                    value={form.values.status}
                    onChange={(e: any) => {
                      const value = e.target.value;
                      if (
                        value == "approved" &&
                        form.values.payments.length == 0
                      ) {
                        addPayment();
                      } else if (value != "approved") {
                        form.setValue("payments", []);
                      }
                      form.setValue("status", value);
                    }}
                    options={OrderStatusActions.filter((item) =>
                      OrderStatusTransitions[order.status]?.includes(item.value)
                    )}
                  />
                </Show>
              </div>
            </div>

            <div className="mt-4">
              <Show when={order.status == "cancelled"}>
                <Button
                  color="red"
                  onClick={() => {
                    store.display.show("delete_order", {
                      callback: () => {
                        // store.display.hide(name);
                        navigate({ to: "/orders/orders" });
                      },
                    });
                  }}
                >
                  {t("Delete order")}
                </Button>
              </Show>
            </div>

            <Show when={form.values.status == "approved"}>
              <ProcessPayment
                form={form}
                allowCashDetails={true}
                accountClass="col-span-2"
                net_amount={net_amount()}
              />
              <Button
                onClick={addPayment}
                className="mt-2 border-none bg-transparent text-primary-500 font-semibold hover:underline hover:bg-transparent px-0"
              >
                <Plus className="w-4 h-4" />
                {t("Add Payment")}
              </Button>
            </Show>

            <Show when={order.status == "completed"}>
              <PayableTransaction payable={order} />
            </Show>
          </div>
        </aside>
        <aside className="w-full md:w-2/3 ">
          <div className="bg-white rounded-md border border-gray-200 border-gray-200 p-6">
            <div className="mt-4">
              <SimpleTable
                items={order.sales}
                rowClass="hover:bg-white"
                cellClass="px-2"
                containerClass="shadow-none border border-gray-200"
                columns={SalesTableColumns({ store }).filter(
                  (col: any) => col.name != "actions"
                )}
              />
              <div className="bg-gray-100 mt-2 rounded-md p-4 flex justify-end">
                <div className="w-full">
                  <PriceItem
                    label={t("Total Amount")}
                    value={getTotal(order.sales)}
                  />
                  <div className="border-b border-gray-300 border-dashed my-1"></div>
                  <PriceItem
                    label={t("Total purchasing price")}
                    value={getPurchaseTotal(order.sales)}
                    color="text-red-800"
                  />
                  <PriceItem
                    label={t("Delivery fee")}
                    value={order.delivery_fee}
                    color="text-green-800"
                  />

                  <PriceItem
                    label={t("Discount")}
                    value={order.discount}
                    color="text-red-800"
                  />

                  <PriceItem
                    label={t("Tax")}
                    value={order.tax}
                    color="text-green-800"
                    surfix=" (%)"
                  />
                  <PriceItem
                    label={t("Profit")}
                    value={getProfit({
                      sales: order.sales,
                      tax: order.tax,
                      ...pick(order, ["discount", "delivery_fee"]),
                    })}
                    color={cn("text-green-800", {
                      "text-red-800":
                        getProfit({
                          sales: order.sales,
                          tax: 0,
                          ...pick(form.values, ["discount", "delivery_fee"]),
                        }) < 0,
                    })}
                  />

                  <div className="border-b border-gray-300 border-dashed my-1"></div>
                  <PriceItem label="Net Amount" value={getNetAmount(order)} />
                </div>
              </div>
            </div>
          </div>
        </aside>
      </section>
    </div>
  );
}
